import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AdminSidebar } from '@/components/AdminSidebar';
import { verifyAdmin, getAchievements, createAchievement, updateAchievement, deleteAchievement } from '@/lib/api';
import { toast } from 'sonner';

const emptyForm = { title: '', description: '', date: '', link: '', is_active: true };

export default function AdminAchievements() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [achievements, setAchievements] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const data = await getAchievements(false);
      setAchievements(data);
    } catch (error) {
      toast.error('Failed to load milestones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const user = await verifyAdmin();
      if (!user) {
        navigate('/admin');
        return;
      }
      if (user.role && user.role !== 'admin') {
        navigate('/admin/dashboard');
        return;
      }
      fetchData();
    };
    checkAuth();
  }, [navigate, fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.date) {
      toast.error('Title and date are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, link: form.link || null };
      if (editingId) {
        await updateAchievement(editingId, payload);
        toast.success('Milestone updated');
      } else {
        await createAchievement(payload);
        toast.success('Milestone added');
      }
      setForm(emptyForm);
      setEditingId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save milestone');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({ title: a.title, description: a.description || '', date: a.date || '', link: a.link || '', is_active: a.is_active !== false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async () => {
    try {
      await deleteAchievement(deleteId);
      toast.success('Milestone deleted');
      if (editingId === deleteId) {
        setEditingId(null);
        setForm(emptyForm);
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to delete milestone');
    } finally {
      setDeleteId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-achievements-page">
      <AdminSidebar active="achievements" />

      <main className="lg:ml-64 p-6 lg:p-8 max-w-5xl">
        <h1 className="font-heading text-3xl font-black text-white uppercase mb-2">Our Story</h1>
        <p className="font-body text-text-secondary mb-8">
          Milestones shown on the public <a href="/story" target="_blank" rel="noreferrer" className="text-[#CCFF00] hover:underline">/story</a> timeline.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-surface border border-zinc-800 p-6 mb-8 space-y-4" data-testid="achievement-form">
          <p className="font-mono text-xs text-text-secondary uppercase">{editingId ? 'Edit Milestone' : 'New Milestone'}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Bormula hits 1M followers"
                className="mt-2 bg-black border-zinc-800 text-white focus:border-[#CCFF00]"
                data-testid="achievement-title-input"
              />
            </div>
            <div>
              <Label className="text-white">Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-2 bg-black border-zinc-800 text-white focus:border-[#CCFF00]"
                data-testid="achievement-date-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-white">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Short story behind this milestone..."
              rows={3}
              className="mt-2 bg-black border-zinc-800 text-white focus:border-[#CCFF00] resize-none"
              data-testid="achievement-description-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <Label className="text-white">Link (optional)</Label>
              <Input
                type="url"
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                placeholder="https://..."
                className="mt-2 bg-black border-zinc-800 text-white focus:border-[#CCFF00]"
                data-testid="achievement-link-input"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-black border border-zinc-800">
              <Label className="text-white">Visible on site</Label>
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                data-testid="achievement-active-switch"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Button type="submit" disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide" data-testid="save-achievement-button">
              <Plus className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : editingId ? 'Update Milestone' : 'Add Milestone'}
            </Button>
            {editingId && (
              <Button type="button" variant="outline" className="border-zinc-800 text-white hover:bg-white/10" onClick={() => { setEditingId(null); setForm(emptyForm); }}>
                Cancel Edit
              </Button>
            )}
          </div>
        </form>

        {/* List */}
        <div className="space-y-3" data-testid="achievements-list">
          {achievements.length === 0 && (
            <p className="p-8 text-center font-body text-text-secondary bg-surface border border-zinc-800">No milestones yet. Add your first one above.</p>
          )}
          {achievements.map((a) => (
            <div key={a.id} className="bg-surface border border-zinc-800 p-5 flex items-start justify-between gap-4" data-testid={`achievement-row-${a.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-xs text-[#CCFF00] uppercase">{a.date}</span>
                  {a.is_active === false && (
                    <span className="font-mono text-[10px] uppercase px-2 py-0.5 bg-zinc-800 text-text-secondary">Hidden</span>
                  )}
                </div>
                <p className="font-heading text-lg font-bold text-white mt-1">{a.title}</p>
                {a.description && <p className="font-body text-sm text-text-secondary mt-1 line-clamp-2">{a.description}</p>}
                {a.link && (
                  <a href={a.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#CCFF00] hover:underline mt-1">
                    <ExternalLink className="w-3 h-3" /> {a.link}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="ghost" size="sm" className="text-text-secondary hover:text-white hover:bg-white/10" onClick={() => startEdit(a)} data-testid={`edit-achievement-${a.id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" className="text-text-secondary hover:text-red-400 hover:bg-red-500/10" onClick={() => setDeleteId(a.id)} data-testid={`delete-achievement-${a.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </main>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-surface border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Milestone</AlertDialogTitle>
            <AlertDialogDescription className="text-text-secondary">
              This will remove the milestone from the public Our Story timeline.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-800 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 text-white hover:bg-red-600" data-testid="confirm-delete-achievement">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
